import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AmcLambdaFetch, awaitJSON } from '@src/utils/fetch-utils';
import {
  AmcNewToBrandBehaviorRow,
  AmcNewToBrandOverview,
  AmcNewToBrandOverviewGraphRow,
  AmcNewToBrandPurchaseRow,
  AmcNewToBrandResponse,
  AmcNewToBrandTableRow,
  AmcNewToBrandVsRepeatBehavior
} from '../amc-new-to-brand-types';

const sanitizeTableRow = (row: AmcNewToBrandTableRow): AmcNewToBrandTableRow => ({
  ...row,
  customersConverted: Number(row.customersConverted),
  ntbCustomers: Number(row.ntbCustomers),
  ntbPurchases: Number(row.ntbPurchases),
  ntbRate: Number(row.ntbRate),
  ntbRepeat: Number(row.ntbRepeat),
  ntbRepeatRate: Number(row.ntbRepeatRate),
  purchases: Number(row.purchases)
});

const sanitizeOverviewGraphRow = (
  row: AmcNewToBrandOverviewGraphRow
): AmcNewToBrandOverviewGraphRow => ({
  ...row,
  ntbCpa: Number(row.ntbCpa),
  ntbRoas: Number(row.ntbRoas)
});

const sanitizeOverview = (overview: AmcNewToBrandOverview): AmcNewToBrandOverview => ({
  cost: {
    cpaAll: Number(overview.cost.cpaAll),
    cpaNtb: Number(overview.cost.cpaNtb),
    ntbCustomers: Number(overview.cost.ntbCustomers),
    spend: Number(overview.cost.spend)
  },
  revenue: {
    ntbRoas: Number(overview.revenue.ntbRoas),
    ntbSales: Number(overview.revenue.ntbSales),
    roasAll: Number(overview.revenue.roasAll),
    spend: Number(overview.revenue.spend)
  }
});

// Adds the *Percent fields the stacked bar charts render (percent of the two-series total for
// that month) — bpm-frontend computed these client-side rather than having the API return them.
const sanitizeBehaviorRow = (row: AmcNewToBrandBehaviorRow): AmcNewToBrandBehaviorRow => {
  const ntbCustomers = Number(row.ntbCustomers);
  const ntbRepeatCustomers = Number(row.ntbRepeatCustomers);
  const total = ntbCustomers + ntbRepeatCustomers;
  return {
    ...row,
    ntbCustomers,
    ntbRepeatCustomers,
    ntbCustomersPercent: total > 0 ? (ntbCustomers / total) * 100 : 0,
    ntbRepeatCustomersPercent: total > 0 ? (ntbRepeatCustomers / total) * 100 : 0
  };
};

const sanitizePurchaseRow = (row: AmcNewToBrandPurchaseRow): AmcNewToBrandPurchaseRow => {
  const ntbCustomers = Number(row.ntbCustomers);
  const repeatCustomers = Number(row.repeatCustomers);
  const total = ntbCustomers + repeatCustomers;
  return {
    ...row,
    ntbCustomers,
    repeatCustomers,
    ntbCustomersPercent: total > 0 ? (ntbCustomers / total) * 100 : 0,
    repeatCustomersPercent: total > 0 ? (repeatCustomers / total) * 100 : 0
  };
};

const sanitizeVsRepeatBehavior = (
  data: AmcNewToBrandVsRepeatBehavior
): AmcNewToBrandVsRepeatBehavior => ({
  customers: data.customers.map(sanitizeBehaviorRow),
  purchases: data.purchases.map(sanitizePurchaseRow)
});

const fetchAmcNewToBrandData = async (
  amcInstanceId: string,
  startDate: string,
  endDate: string
): Promise<AmcNewToBrandResponse> => {
  const response = await AmcLambdaFetch('/getNtbReport', {
    params: { amcInstanceId, startDate, endDate, duration: 'monthly' }
  });
  const json = await awaitJSON<AmcNewToBrandResponse>(response);
  return {
    ...json,
    behaviorByGroup: json.behaviorByGroup.map(sanitizeTableRow),
    overview: sanitizeOverview(json.overview),
    overviewGraph: json.overviewGraph.map(sanitizeOverviewGraphRow),
    ntbVsRepeatBehavior: sanitizeVsRepeatBehavior(json.ntbVsRepeatBehavior)
  };
};

export const useAmcNewToBrandData = (
  amcInstanceId: string | undefined,
  startDate: string,
  endDate: string
): ReturnType<typeof useQuery<AmcNewToBrandResponse>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['amcNewToBrandData', amcInstanceId, startDate, endDate],
    queryFn: () => {
      if (!amcInstanceId) throw new Error('Missing AMC instance id');
      return fetchAmcNewToBrandData(amcInstanceId, startDate, endDate);
    },
    enabled: Boolean(amcInstanceId),
    placeholderData: keepPreviousData
  });
